import React, { useEffect } from "react";
import { IconButton, IconType, InformationMessage, usePopupFormManager } from "features/nekst-widgets";
import { Dropdown, FormTemplate, Header5, LoadingAnimation, Row6x6x, useFormContextRequired } from "ui-builder";
import { ManageCommissionAndClosingDateWrapper } from "feature-toggle";
import {
  ProjectDetailsField,
  ProjectDetailsFieldType,
  ProjectType,
  TeamSetupArea, useEventsPublisher,
  useProjectClosingDateFieldService,
  useProjectCommissionFieldService,
  useProjectDetailsFieldsService
} from "nekst-api";
import { COMMISSION_SETTINGS_UPDATED, useProjectTypeHelper } from "features/tasks-forms";
import { useTeamSetupAreasContextRequired } from "../../settings/markets/TeamSetupAreasContext";
import { useDataLoaderNew } from "data-loader";
import { TeamSetupAreaCompactDropdown } from "./TeamSetupAreaCompactDropdown";

function ClosingDateDropdown(
  props: {
    setupArea: TeamSetupArea,
    detailFields: ProjectDetailsField[],
    projectType: ProjectType,
  }
) {
  const source = `map.${props.setupArea.id}.${props.projectType}.closingDateFieldId`;

  const formContext = useFormContextRequired();

  const projectClosingDateService = useProjectClosingDateFieldService();

  return (
    <Dropdown
      options={props.detailFields
        .filter((item) => item.fieldType === ProjectDetailsFieldType.DATE)
        .map((item) => ({
          label: item.name,
          value: item.id
        }))}
      onChangeCallback={async (data) => {
        try {
          const newValue = data[source]
          formContext.onChangeCallback({
            [source]: data[source],
          });
          formContext.setDisabled(true);

          await projectClosingDateService.updateIsClosingDateField(
            props.projectType,
            newValue,
            true,
            props.setupArea.id,
          );
        } finally {
          formContext.setDisabled(false);
        }

      }}
      source={source}
      isClearable={false}
      label="Closing Date"
    />
  );
}

function CommissionFieldDropdown(
  props: {
    setupArea: TeamSetupArea,
    detailFields: ProjectDetailsField[],
    projectType: ProjectType,
  }
) {
  const source = `map.${props.setupArea.id}.${props.projectType}.commissionFieldId`;

  const formContext = useFormContextRequired();

  const projectCommissionFieldService = useProjectCommissionFieldService();

  return (
    <Dropdown
      options={props.detailFields
        .filter((item) => item.fieldType === ProjectDetailsFieldType.PRICE)
        .map((item) => ({
          label: item.name,
          value: item.id
        }))}
      onChangeCallback={async (data) => {
        try {
          const newValue = data[source]
          formContext.onChangeCallback({
            [source]: data[source],
          });
          formContext.setDisabled(true);

          await projectCommissionFieldService.updateIsCommissionField(
            props.projectType,
            newValue,
            true,
            props.setupArea.id,
          );
        } finally {
          formContext.setDisabled(false);
        }
      }}
      source={source}
      isClearable={false}
      label="Commission Field"
    />
  );
}

function ProjectTypeFormInputs(
  props: {
    setupArea: TeamSetupArea,
    projectType: ProjectType,
    detailFields: ProjectDetailsField[]
  }
) {
  const projectTypeHelper = useProjectTypeHelper();
  return (
    <>
      <Header5>{projectTypeHelper.getLabel(props.projectType)}</Header5>
      <Row6x6x>
        <ClosingDateDropdown
          setupArea={props.setupArea}
          detailFields={props.detailFields}
          projectType={props.projectType} />
        <CommissionFieldDropdown
          setupArea={props.setupArea}
          detailFields={props.detailFields}
          projectType={props.projectType}
        />
      </Row6x6x>
    </>
  );
}

function SetupAreaFormInputs(props: {
  setupArea: TeamSetupArea,
  detailFieldsMap: Record<ProjectType, ProjectDetailsField[]>
}) {
  return (
    <>
      <ProjectTypeFormInputs
        setupArea={props.setupArea}
        projectType={ProjectType.PENDING_BUYER}
        detailFields={props.detailFieldsMap[ProjectType.PENDING_BUYER] || []}
      />
      <ProjectTypeFormInputs
        setupArea={props.setupArea}
        projectType={ProjectType.PENDING_SELLER}
        detailFields={props.detailFieldsMap[ProjectType.PENDING_SELLER] || []}
      />
      <ProjectTypeFormInputs
        setupArea={props.setupArea}
        projectType={ProjectType.PENDING_DUAL}
        detailFields={props.detailFieldsMap[ProjectType.PENDING_DUAL] || []}
      />

    </>
  )
}

function ManageCommissionAndClosingDatesForm() {
  const setupAreasContext = useTeamSetupAreasContextRequired();

  const projectDetailsFieldsService = useProjectDetailsFieldsService();

  const [selectedSetupArea, setSelectedSetupArea] = React.useState<TeamSetupArea>(setupAreasContext.teamSetupAreas[0] ?? null);

  const eventsPublisher = useEventsPublisher();

  useEffect(() => {
    return () => {
      eventsPublisher.publish(COMMISSION_SETTINGS_UPDATED, {});
    }
  }, []);

  const loadFieldsFunc = async () => {
    const [buyerFields, sellerFields, dualFields] = await Promise.all([
      projectDetailsFieldsService.getAllSetupAreasFields(ProjectType.PENDING_BUYER),
      projectDetailsFieldsService.getAllSetupAreasFields(ProjectType.PENDING_SELLER),
      projectDetailsFieldsService.getAllSetupAreasFields(ProjectType.PENDING_DUAL),
    ]);

    const result: Record<number, Record<ProjectType, ProjectDetailsField[]>> = {};

    const processFields = (fields: ProjectDetailsField[], projectType: ProjectType) => {
      for (const field of fields) {
        const setupAreaId = field._setupArea?.id;
        if (!setupAreaId) continue;

        if (!result[setupAreaId]) {
          result[setupAreaId] = {} as Record<ProjectType, ProjectDetailsField[]>;
        }
        if (!result[setupAreaId][projectType]) {
          result[setupAreaId][projectType] = [];
        }
        result[setupAreaId][projectType].push(field);
      }
    };

    processFields(buyerFields, ProjectType.PENDING_BUYER);
    processFields(sellerFields, ProjectType.PENDING_SELLER);
    processFields(dualFields, ProjectType.PENDING_DUAL);

    return result;
  }

  const { data: fieldsBySetupArea } = useDataLoaderNew(loadFieldsFunc);

  const buildInitialData = () => {
    if (!fieldsBySetupArea) return {};

    const map: Record<number, Record<string, { closingDateFieldId?: number, commissionFieldId?: number }>> = {};

    Object.entries(fieldsBySetupArea).forEach(([setupAreaId, projectTypeMap]) => {
      map[Number(setupAreaId)] = {};

      Object.entries(projectTypeMap).forEach(([projectType, fields]) => {
        const closingDateField = fields.find(f => f.isClosingDateField);
        const commissionField = fields.find(f => f.isCommissionField);

        map[Number(setupAreaId)][projectType] = {
          closingDateFieldId: closingDateField?.id,
          commissionFieldId: commissionField?.id
        };
      });
    });

    return { map };
  };

  if (fieldsBySetupArea) {
    return (
      <FormTemplate
        title="Commission & Closing Date Settings"
        submitFormFunc={async () => {
          // no action
        }}
        initialData={buildInitialData() as any}
        buttons={null}
      >
        <InformationMessage withoutMargin>
          Select the appropriate detail fields that should be used for tracking the number of sales & the commission amount for your market(s). Changes are saved automatically when you make a selection.
        </InformationMessage>
        <TeamSetupAreaCompactDropdown
          setupAreas={setupAreasContext.teamSetupAreas}
          value={selectedSetupArea.id}
          onChangeCallback={(newId) => {
            const newSetupArea = setupAreasContext.teamSetupAreas.find((area) => area.id === newId);
            if (newSetupArea) {
              setSelectedSetupArea(newSetupArea);
            }
          }}
        />
        <SetupAreaFormInputs
          setupArea={selectedSetupArea}
          detailFieldsMap={fieldsBySetupArea[selectedSetupArea.id] || {}}
        />
      </FormTemplate>
    );
  } else {
    return (<LoadingAnimation />);
  }
}


export function ManageCommissionAndClosingDatesButton() {
  const popupFormManager = usePopupFormManager();

  return (
    <ManageCommissionAndClosingDateWrapper>
      <IconButton
        onClick={async () => {
          popupFormManager.openForm(<ManageCommissionAndClosingDatesForm />);
        }}
        type={IconType.GEAR}
        title="Manage Commission & Closing Date Settings"
      />
    </ManageCommissionAndClosingDateWrapper>
  )
}
